"use client"

import { iconsMap } from "@/public/icons/iconsMap"
import styled from "@emotion/styled"
import {
  Box,
  Button,
  FormControl,
  List,
  ListItem,
  SwipeableDrawer,
  Typography,
} from "@mui/material"
import { useEffect, useState } from "react"
import { colors, mediaQueries, sizes, useIsDesktop } from "ui"
import { transformProperties } from "utils/simplifyData"
import CommonTitle from "../common/bit/CommonTitle"

const StyledMoreInfoContainer = styled(Box)`
  position: relative;
  display: flex;
  ${mediaQueries.mobile} {
    justify-content: space-between;
    flex-direction: column;
    width: 90%;
    gap: 0;
  }
  ${mediaQueries.tablet} {
    width: 90%;
    flex-direction: column;
    gap: 40px;
  }

  ${mediaQueries.desktopM} {
    display: grid;
    grid-template-columns: 450px 550px;
    column-gap: 130px;
  }
`
const StyledTitleAndBulltersContainer = styled(Box)`
  width: 90%;
  ${mediaQueries.desktopM} {
    width: 550px;
  }
`

const StyledMoreInfoTitles = styled(List)`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border-radius: 30px;
  width: 450px;
  height: fit-content;
`
const StyledListItem = styled(ListItem)<{ selected?: boolean }>`
  padding: 30px;
  cursor: pointer;
  outline: none; // Remove red border on selected/active
  h5 {
    text-align: start;
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${colors.borderGray};
  }
`

const StyledListItemTypography = styled(Typography)<{ selected?: boolean }>`
  text-align: start;
  width: 100%;
  color: ${(props) => (props.selected ? colors.red : colors.black)};
`

const StyledBullets = styled(Box)<{ component?: string }>`
  color: ${colors.black};
  font-size: 18px;
  margin-bottom: 40px;

  ul {
    padding-inline-start: 18px;
    ${mediaQueries.mobile} {
      padding-inline-start: 0;
    }
    ${mediaQueries.tablet} {
      padding-inline-start: 0;
    }
    li {
      position: relative;
      text-align: start;
      margin-bottom: 12px;
      font-weight: 400;
      list-style-type: none;
      padding-left: 24px;

      &::before {
        content: url("/icons/check.svg");
        position: absolute;
        left: 0;
        top: 0;
      }
    }
  }
`

const StyledFormControl = styled(FormControl)`
  height: 75px;
  margin-bottom: ${sizes.baseTriple};
  .MuiInputBase-root {
    height: 100%;
  }
`

const StyledDrawer = styled(SwipeableDrawer)`
  & .MuiDrawer-paper {
    display: flex;
    align-items: center;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    max-height: 70vh;
  }
`
const StyledDrawerLine = styled("span")`
  position: absolute;
  display: flex;
  top: ${sizes.baseDouble};
  width: 162.199px;
  height: ${sizes.baseHalf};
  border-radius: ${sizes.baseDouble};
  background-color: ${colors.black};
`

const StyledDrawerList = styled(List)`
  display: flex;
  flex-direction: column;
  padding: ${sizes.baseSix} 0;
`

const StyledDrawerListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "selectedItem",
})<{ selectedItem?: boolean }>`
  cursor: pointer;
  width: 100%;
  height: 100%;
  padding-left: 0;
  padding-right: 0;
  order: ${(props) =>
    props.selectedItem
      ? 1
      : 2}; // Move the selected item to the top of the list
`

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "outlineColor",
})<{ outlineColor?: string }>`
  justify-content: space-between;
  text-align: left;
  height: 75px;
  width: 100%;
  font-size: 18px;
  font-weight: 800;
  border-radius: ${sizes.borderRadius};
  border: 1px solid
    ${(props) => (props.outlineColor ? props.outlineColor : "transparent")};
  color: ${(props) => (props.outlineColor ? props.outlineColor : colors.red)};
  box-shadow: ${(props) =>
    props.outlineColor === colors.red
      ? "-4.3px 9px 33.8px 0px rgba(186, 12, 47, 0.09)"
      : "none"};
  &:focus {
    background-color: ${colors.white};
  }
`

const StyledText = styled(Typography)`
  ${mediaQueries.desktop} {
    font-size: ${sizes.baseDouble};
  }
`
const StyledCommonTitle = styled(CommonTitle)`
  margin: 0;
  align-items: flex-start;
  h2 {
    text-align: start;
    padding-bottom: 40px;
  }
`

import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import { useRef } from "react"

const ListProductsSelect = ({ itemsList, onTabClick }: any) => {
  // Prepare tab data from blocks
  const blocks = itemsList?.blocks || []
  const tabPanels = blocks.map((item: any) =>
    transformProperties(item?.properties),
  )

  // Initialize isDesktop to true on the server, and use the hook on the client
  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])
  const clientIsDesktop = useIsDesktop()
  const isDesktop = isClient ? clientIsDesktop : true // Default to true on server

  const [selectedTab, setSelectedTab] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const ArrowIcon = iconsMap["arrowDownSelect"]

  // For keyboard navigation
  const tabRefs = useRef<(HTMLLIElement | null)[]>([])

  // Keyboard navigation for tabs
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (!isDesktop) return
    let newIndex = selectedTab
    if (event.key === "ArrowDown") {
      newIndex = (selectedTab + 1) % blocks.length
      setSelectedTab(newIndex)
      tabRefs.current[newIndex]?.focus()
      event.preventDefault()
    } else if (event.key === "ArrowUp") {
      newIndex = (selectedTab - 1 + blocks.length) % blocks.length
      setSelectedTab(newIndex)
      tabRefs.current[newIndex]?.focus()
      event.preventDefault()
    } else if (event.key === "Home") {
      setSelectedTab(0)
      tabRefs.current[0]?.focus()
      event.preventDefault()
    } else if (event.key === "End") {
      setSelectedTab(blocks.length - 1)
      tabRefs.current[blocks.length - 1]?.focus()
      event.preventDefault()
    }
  }

  // Desktop: Accessible vertical tabs
  return (
    <StyledMoreInfoContainer>
      <>
        {isDesktop && (
          <StyledMoreInfoTitles role="tablist" aria-orientation="vertical">
            {blocks.map((item: any, index: number) => {
              const { subtitle } = transformProperties(item?.properties)
              return (
                <StyledListItem
                  role="tab"
                  aria-selected={selectedTab === index}
                  aria-controls={`tabpanel-${index}`}
                  id={`tab-${index}`}
                  tabIndex={selectedTab === index ? 0 : -1}
                  ref={(el) => (tabRefs.current[index] = el)}
                  onClick={() => {
                    setSelectedTab(index)
                    onTabClick(subtitle?.value)
                  }}
                  onKeyDown={handleTabKeyDown}
                  key={`ListProductsSelect-StyledListItem-${index}`}
                  selected={selectedTab === index}
                >
                  <StyledListItemTypography
                    variant="h5"
                    selected={selectedTab === index}
                  >
                    {subtitle?.value}
                  </StyledListItemTypography>
                </StyledListItem>
              )
            })}
          </StyledMoreInfoTitles>
        )}
        {!isDesktop && (
          <StyledFormControl>
            <StyledButton
              variant="outlined"
              onClick={() => setDrawerOpen(true)}
              endIcon={<ArrowIcon />}
              aria-haspopup="listbox"
              aria-expanded={drawerOpen}
              aria-controls="product-select-drawer"
            >
              {tabPanels[selectedTab]?.subtitle?.value}
            </StyledButton>
            <StyledDrawer
              anchor="bottom"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              onOpen={() => setDrawerOpen(true)}
              id="product-select-drawer"
              role="dialog"
              aria-modal="true"
            >
              <StyledDrawerLine />
              <StyledDrawerList role="listbox" aria-label="Select product">
                {blocks.map((item: any, index: number) => {
                  const { subtitle } = transformProperties(item?.properties)
                  return (
                    <StyledDrawerListItem
                      key={`ListProductsSelect-DrawerItem-${index}`}
                      selectedItem={selectedTab === index}
                      role="option"
                      aria-selected={selectedTab === index}
                      tabIndex={0}
                      onClick={() => {
                        setSelectedTab(index)
                        setDrawerOpen(false)
                        onTabClick(subtitle?.value)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedTab(index)
                          setDrawerOpen(false)
                        }
                      }}
                    >
                      <StyledButton
                        outlineColor={
                          selectedTab === index ? colors.red : colors.gray
                        }
                        variant="outlined"
                        endIcon={selectedTab === index ? <ArrowIcon /> : null}
                      >
                        {subtitle?.value}
                      </StyledButton>
                    </StyledDrawerListItem>
                  )
                })}
              </StyledDrawerList>
            </StyledDrawer>
          </StyledFormControl>
        )}
      </>
      {/* Tab Panels: always mounted, only selected is visible */}
      <StyledTitleAndBulltersContainer>
        {tabPanels.map((panel: any, idx: number) => (
          <div
            key={`tabpanel-${idx}`}
            role="tabpanel"
            id={`tabpanel-${idx}`}
            aria-labelledby={`tab-${idx}`}
            hidden={selectedTab !== idx}
            aria-hidden={selectedTab !== idx}
            style={{
              display: selectedTab === idx ? "block" : "none",
              width: "100%",
            }}
          >
            <StyledCommonTitle title={panel.title?.value} />
            {panel.text?.value && (
              <StyledText variant="body1" color="black">
                {panel.text.value}
              </StyledText>
            )}
            {panel.bullets?.richText && (
              <StyledBullets
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(panel.bullets.richText),
                }}
              />
            )}
          </div>
        ))}
      </StyledTitleAndBulltersContainer>
    </StyledMoreInfoContainer>
  )
}

export default ListProductsSelect
